import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { ApiConfiguration } from '../../api/api-configuration';
import {
  dashboardProjectsControllerGetProjectContributorsV1,
  DashboardProjectsControllerGetProjectContributorsV1$Params,
} from '../../api/fn/dashboard-projects/dashboard-projects-controller-get-project-contributors-v-1';
import { ContributorResponseDto } from '../../api/models/contributor-response-dto';
import { CurrencyFormatPipe } from '../../pipes/currency-format.pipe';

export interface Contributor {
  rank: number;
  walletAddress: string;
  contributedAmount: string;
}

@Component({
  selector: 'app-contributors-modal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    TooltipModule,
    TranslateModule,
    PaginatorModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './contributors-modal.component.html',
  styleUrl: './contributors-modal.component.css',
})
export class ContributorsModalComponent implements OnInit {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly clipboard = inject(Clipboard);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly http = inject(HttpClient);
  private readonly currencyPipe = new CurrencyFormatPipe();

  protected readonly projectId: string = this.config.data?.projectId || '';
  protected readonly rowsPerPage = 50;

  // Pagination state
  protected readonly currentPage = signal(1);
  protected readonly totalRecords = signal(0);
  protected readonly totalRaised = signal(0);
  protected readonly loading = signal(false);
  protected readonly contributors = signal<Contributor[]>([]);

  // Computed values
  protected readonly totalContributors = computed(() => this.totalRecords());
  protected readonly first = computed(() => (this.currentPage() - 1) * this.rowsPerPage);

  ngOnInit(): void {
    if (this.projectId) {
      this.loadContributors(1);
    }
  }

  private async loadContributors(page: number): Promise<void> {
    if (!this.projectId) return;

    this.loading.set(true);

    try {
      const params: DashboardProjectsControllerGetProjectContributorsV1$Params = {
        projectId: this.projectId,
        page,
        limit: this.rowsPerPage,
      };

      const response = await firstValueFrom(
        dashboardProjectsControllerGetProjectContributorsV1(
          this.http,
          this.apiConfig.rootUrl,
          params
        )
      );

      const body = response.body!;

      // Format contributors for display
      const formattedContributors: Contributor[] = body.contributors.map(
        (c: ContributorResponseDto) => ({
          rank: c.rank,
          walletAddress: c.walletAddress,
          contributedAmount: `${this.currencyPipe.transform(c.totalContributed)} USDT`,
        })
      );

      this.contributors.set(formattedContributors);
      this.totalRecords.set(body.meta.totalItems);
      this.totalRaised.set(body.totalRaised);
      this.currentPage.set(page);
    } catch (error) {
      console.error('Failed to load contributors:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('projectInsight.contributorsModal.loadError'),
        life: 3000,
      });
    } finally {
      this.loading.set(false);
    }
  }

  onPageChange(event: PaginatorState): void {
    const newPage = Math.floor((event.first ?? 0) / this.rowsPerPage) + 1;
    this.loadContributors(newPage);
  }

  onClose(): void {
    this.ref.close();
  }

  onCopyAddress(address: string): void {
    this.clipboard.copy(address);
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.copied'),
      detail: this.translateService.instant('common.copiedToClipboard'),
      life: 3000,
    });
  }

  // Truncate wallet address to show first 6 and last 4 characters
  getTruncatedAddress(address: string): string {
    if (!address || address.length < 10) return address;
    return `${address.substring(0, 6)}…${address.substring(address.length - 4)}`;
  }

  onViewOnExplorer(address: string): void {
    // Open block explorer in new tab (adjust URL based on network)
    const explorerUrl = `https://etherscan.io/address/${address}`;
    window.open(explorerUrl, '_blank');
  }

  onExportList(): void {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('projectInsight.contributorsModal.noDataToExport'),
        life: 3000,
      });
      return;
    }

    // Download CSV from backend endpoint (streaming, handles large datasets - exports ALL contributors)
    const exportUrl = `${this.apiConfig.rootUrl}/v1/dashboard/projects/${this.projectId}/contributors/export`;

    // Create a hidden link and trigger download
    const link = document.createElement('a');
    link.href = exportUrl;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.success'),
      detail: this.translateService.instant('projectInsight.contributorsModal.exportSuccess'),
      life: 3000,
    });
  }
}
